import axios from '../../../axios';
import {
  CandidateResume,
  ICandidate,
  IHarvestBot,
  IHarvestBotCustomize,
  IHarvestBotSearch,
} from '../../../types';
import IContactAction from '../../../types/IContactAction';
import IBooleanQueryBuilderDTO from '../../JobSearchService/types/IBooleanQueryBuilderDTO';
import AISmartSourceSearch from '../types/AISmartSourceSearch';
import {
  AutomatedEmailCampaignType,
  EmailLog,
  IAutomatedEmailCampaignListPayload,
  IAutomatedEmailCampaignSetting,
  IAutomatedEmailCampaignSettingListPayload,
  IAutomatedCampaignConfig,
} from '../types/IAutomatedEmailCampaign';
import { ICandidateEmailLogPayload } from '../types/ICandidateEmailLog';
import ICandidateInteractionActionPipeline from '../types/ICandidateInteractionActionPipeline';
import ICandidatesMergeDTO from '../types/ICandidatesMergeDTO';
import ICandidateUpdateLinkDTO from '../types/ICandidateUpdateLinkDTO';
import IJobCandidateInteraction from '../types/IJobCandidateInteraction';
import IJobCandidateInteractionResponse from '../types/IJobCandidateInteractionResponse';
import IJobCandidateSummary from '../types/IJobCandidateSummary';
import IPipelineUpdateLinkDTO from '../types/IPipelineUpdateLinkDTO';
import ISearchQueryParam from '../types/ISearchQueryParam';
import { IStageStatusPayload } from '../types/IStageStatusPayload';
import ISubmissionFormSettings from '../types/ISubmissionFormSettings';
import PipelineResultCommSettings from '../types/PipelineResultCommSettings';
import UpdateCandidateDetails from '../types/UpdateCandidateDetails';

const url = '/candidatesearch/api/';

export const candidateSearchAdvancedQuery = (jobCandidateInteraction: IJobCandidateInteraction) =>
  axios.post(`${url}mapping/search`, jobCandidateInteraction);

export const isCandidateDataAvailable = (searchQueryParams: ISearchQueryParam) =>
  axios.post(url + 'candidate/do-candidate-exist', searchQueryParams);

export const candidateSearchAdvancedQueryWithAggregations = (
  searchQueryParams: ISearchQueryParam
) => axios.post(url + 'mapping/_search/advanced/with-aggregations', searchQueryParams);

export const getDailyConversationCount = () =>
  axios.get(url + 'mapping/get-temp-conversation-count');

export const getInteractionForCandidateIdAndJobId = (candidateId: string, jobId: string) =>
  axios.get(`${url}mapping/${candidateId}/${jobId}`);

export const getCandidateStatsByJobIdsPostgres = (
  jobCandidateInteraction: IJobCandidateInteraction
) => axios.post(`${url}mapping/candidate-job-stats-by-jobIds`, jobCandidateInteraction);

export const createOrGetMapping = (
  jobCandidateInteraction: IJobCandidateInteractionResponse & {
    mostRecentConversation: IContactAction;
  }
) => axios.post(url + 'mapping/create-or-get-mapping', jobCandidateInteraction);

export const createOrGetMappingForEdit = (
  jobCandidateInteraction: IJobCandidateInteractionResponse
) => axios.post(url + 'mapping/create-or-get-mapping-for-edit', jobCandidateInteraction);

export const getCandidateSearch = (
  candidateSearch: IJobCandidateInteraction,
  buildFullResultSet: boolean
) => axios.post(`${url}candidate/search?${buildFullResultSet}`, candidateSearch);

export const getJobCandidateInteraction = (id: string) => axios.get(`${url}mapping/${id}`);

export const getCandidateById = (id: string) => axios.get(`${url}candidate/${id}`);

export const updateJobCandidateNotesStateStage = (jobCandidateInteraction: IStageStatusPayload) =>
  axios.put(`${url}submission/update-notes-state-stage`, jobCandidateInteraction);

export const getCvSourceSuggestions = (source: string) =>
  axios.get(`${url}mapping/suggestions/cv-source?source=${source}`);

export const getSubmissionSettings = () =>
  axios.get(`${url}candidate-fields/get-submission-setting`);

export const resetDefaultSettings = () =>
  axios.get(`${url}candidate-fields/reset-submission-settings`);

export const saveSubmissionSettings = (payload: ISubmissionFormSettings) =>
  axios.post(`${url}candidate-fields/save-submission-setting`, payload);

export const submitCandidate = (candidate: ICandidate) => axios.post(`${url}submission`, candidate);

export const updateCandidateSubmission = (candidate: ICandidate[]) =>
  axios.put(`${url}submission`, candidate);

export const getSubmissionCount = (emailAndJobId: { candidateEmail: string; jobId: string }) =>
  axios.get(`${url}submission/get-submission-count`, { params: emailAndJobId });

export const getCandidateByCVUrl = (url: { s3URL: string }) =>
  axios.get(`/candidatesearch/api/mapping/candidate-by-cv-url`, { params: url });

export const indexMapping = (candidate: ICandidate) => axios.post(`${url}mapping`, candidate);
export const getCandidateInteractionMessagingPipelines = () =>
  axios.get('api/candidate-interaction-messaging-pipelines');

export const saveCandidateInteractionMessagingPipelines = (
  payload: ICandidateInteractionActionPipeline
) => axios.post('api/candidate-interaction-messaging-pipelines', payload);

export const saveCandidateInteractionPipelineResultCommSettings = (
  payload: PipelineResultCommSettings
) => axios.put('/candidatesearch/api/pipeline-result-settings', payload);

export const getCandidateInteractionPipelineResultCommSettings = () =>
  axios.get(`/candidatesearch/api/pipeline-result-settings`);

export const resetCandidateInteractionPipelineResultCommSettings = (dataType: string) =>
  axios.post(`/candidatesearch/api/pipeline-result-settings/reset-to-default/${dataType}`);
export const resetDefaultCandidateInteractionMessagingPipelines = () =>
  axios.post('api/candidate-interaction-messaging-pipelines/reset-to-default');

export const getCandidateInteractionResultPipelines = () =>
  axios.get('api/candidate-interaction-result-pipelines');

export const saveCandidateInteractionResultPipelines = (
  payload: ICandidateInteractionActionPipeline
) => axios.post('api/candidate-interaction-result-pipelines', payload);

export const resetDefaultCandidateInteractionResultPipelines = () =>
  axios.post('api/candidate-interaction-result-pipelines/reset-to-default');

export const getSimilarJobs = (
  jobCode: string,
  jobCandidateInteraction: IJobCandidateInteraction
) =>
  axios.post(
    `${url}mapping/suggest-teams-recruiters-similar-jobs-for-job/${jobCode}`,
    jobCandidateInteraction
  );

export const getCandidateResumeByCVUrl = (s3url: string, id: string) =>
  axios.get(`${url}mapping/candidate-resume-by-cv-url`, {
    params: { interactionId: id, s3URL: s3url },
  });

export const getRecruiterCalendarEvents = (size: number) =>
  axios.post(`${url}calendar-event/recruiter-events`, {
    sort: 'createdOn',
    sortDirection: 'DESC',
    size: size,
    page: 0,
  });
export const getCandidateHarvestBots = (
  type: string,
  status: string,
  allBots: boolean,
  pageNumber: number
) => axios.get(`${url}harvesting-bot/job-board/${type}/${allBots}/${pageNumber}/${status}`, {});

export const startOrStopHarvestBots = (harvestBots: IHarvestBot) =>
  axios.put(`${url}harvesting-bot/`, harvestBots);

export const startHarvestBots = (harvestBots: IHarvestBotCustomize) =>
  axios.post(`${url}harvesting-bot/`, harvestBots);

export const getCandidateResumeByFileUrl = (fileUrl: string) =>
  axios.get(`${url}candidate/candidate-resume-by-cv-url`, {
    params: { s3URL: fileUrl },
  });
export const searchBots = (data: IHarvestBotSearch) =>
  axios.post('/candidatesearch/api/harvesting-bot/search', data);

export const getCandidateAggregatedData = (searchQueryParams: ISearchQueryParam) =>
  axios.post(`${url}candidate/get-candidate-aggregation-data`, searchQueryParams);

export const loadCandidateByCandidateId = (id: string) => axios.get(`${url}candidate/${id}`);

export const getSubmissionStageStats = (jobCandidateInteraction: IJobCandidateInteraction) =>
  axios.post(`${url}submission/stage-status-count`, jobCandidateInteraction);

export const getAllInteractions = (id: string) =>
  axios.get(`${url}candidate/recent-interactions/${id}/100`);

export const getAllCV = (candidateId: string) =>
  axios.post(`${url}candidate/candidate-cvs/${candidateId}`);

export const getTopCV = (candidateId: string) =>
  axios.get(`${url}candidate/top-candidate-cvs/${candidateId}`);

export const convertWordToPdf = (resumeLocation: string) =>
  axios.post(`${url}candidate/get-original-cv-pdf`, resumeLocation, {
    headers: {
      Accept: 'application/pdf',
    },
    responseType: 'arraybuffer',
  });

export const getLatestCvOfCandidates = (candidateIds: string[]) =>
  axios.post(`${url}candidate/candidates-latest-cv`, candidateIds);

export const getAllOtherDocs = (candidateId: string) =>
  axios.post(`${url}candidate/candidate-other-docs/${candidateId}`);

export const getCandidatesRecentMappings = (CandidateEntities: ICandidate[]) =>
  axios.post(`${url}candidate/get-recent-mappings`, CandidateEntities);

export const getCandidatesRecentSubmissions = (CandidateEntities: ICandidate[]) =>
  axios.post(`${url}candidate/get-recent-submissions`, CandidateEntities);

export const getHarvestingBotsCount = () =>
  axios.get(`${url}harvesting-bot/job-board/harvesting-bots-count`);

export const addingContactLinkInCandidateMaster = (payload: ICandidateUpdateLinkDTO) =>
  axios.post(`${url}candidate/add-contact-link`, payload);

export const updateContactLinkInCandidateMaster = (payload: ICandidateUpdateLinkDTO) =>
  axios.post(`${url}candidate/update-contact-link`, payload);

export const removeContactLinkInCandidateMaster = (payload: ICandidateUpdateLinkDTO) =>
  axios.post(`${url}candidate/remove-contact-link`, payload);

export const eraseCandidatePersonalDataFromMapping = (candidateMappingId: string) =>
  axios.put(`${url}candidate/erase-candidate-personal-data`, candidateMappingId);

export const processCandidateDeleteRequest = (candidateLink: string) =>
  axios.put(`${url}candidate/accept-delete-request/${candidateLink}`);

export const addPieplineContacts = (payload: IPipelineUpdateLinkDTO) =>
  axios.put(`${url}linkedcontacts/add`, payload);

export const updatePieplineContacts = (payload: IPipelineUpdateLinkDTO) =>
  axios.put(`${url}linkedcontacts/update`, payload);

export const deletePieplineContacts = (payload: IPipelineUpdateLinkDTO) =>
  axios.put(`${url}linkedcontacts/delete`, payload);

export const generateKeyWords = (request: IBooleanQueryBuilderDTO) =>
  axios.post(`/wsaiinfinity/keyword-generator`, request);

export const importToVMS = (candidate: ICandidate) =>
  axios.put(`${url}submission/import-to-vms`, candidate);

export const deleteSubmission = (candidate: ICandidate) =>
  axios.put(`${url}submission/delete-submission`, candidate);

export const deleteMapping = (candidate: ICandidate) =>
  axios.put(`${url}mapping/delete-mapping`, candidate);

export const deleteResume = (candidateId: string, index: string) =>
  axios.post(`${url}candidate/delete-candidate-cv/${candidateId}/${index}`);
export const getCandidateJobBoardProfile = (src: string, candidateJobBoardId: string) =>
  axios.get(`${url}candidate/profile-by-job-board/${src}/${candidateJobBoardId}`);
export const demergeCandidate = (candidateId: string, emailId: string) =>
  axios.post(
    `${url}linkedcontacts/explodev6/fixworkspacecandidate?candidateId=${candidateId}&breakLinks=${emailId}`
  );
export const demergeCandidateById = (candidateId: string) =>
  axios.post(`${url}linkedcontacts/explodev6/fixworkspacecandidateall?candidateId=${candidateId}`);

export const bulkUpdateJobCandidateStateStage = (
  jobCandidateInteractions: IJobCandidateInteraction[]
) => axios.put(`${url}submission/bulk-update-state-stage`, jobCandidateInteractions);

export const sharedCandidateDetails = (id: string) => axios.get(`${url}candidate/shared/${id}`);

export const checkExistingMappings = (candidate: ICandidate) =>
  axios.post(`${url}mapping/check-existing-mapping`, candidate);
export const getAISmartSourceMapped = (mapped: AISmartSourceSearch) =>
  axios.post(`${url}ai-smart-source/get-ismapped-by-links`, mapped);

export const getCandidateProfileDetails = (candidateId: string) =>
  axios.get(`${url}candidate/candidate-profile/details`, {
    params: {
      candidateId,
    },
  });
export const updateCandidateDetails = (updateCandidateDetails: UpdateCandidateDetails) =>
  axios.put(`${url}candidate/edit-details`, updateCandidateDetails);

export const getMappingsByCandidateID = (candidateId: string, jobId?: string) =>
  axios.get(`${url}mapping/job-mapping/${candidateId}?jobId=${jobId}`);
export const getCandidateSummary = (submissionId: string) =>
  axios.get(`${url}submission/${submissionId}`);

export const postCandidateSummary = (payload: IJobCandidateSummary) =>
  axios.post(`${url}submission/candidate-job-summary`, payload);

export const getJobTitles = (searchKey: string) =>
  axios.get(`${url}mapping/job-titles/suggestions`, {
    params: {
      searchKey,
    },
  });

export const saveCandidate = (candidate: ICandidate) => axios.post(`${url}candidate`, candidate);

export const getSubmissionJobsByCandidateId = (candidateId: string) =>
  axios.get(`${url}submission/job-submission/${candidateId}`);

export const getSubmissionById = (submissionId: string) =>
  axios.get(`${url}submission/${submissionId}`);

export const getSubmissionsByCandidateIdAndJobId = (candidateId: string, jobId: string) =>
  axios.get(`${url}submission/${candidateId}/${jobId}`);

export const checkExistingCandidates = (candidateEmail: string) =>
  axios.get(`${url}candidate/details?link=${candidateEmail}`);

export const mergeCandidates = (payload: ICandidatesMergeDTO) =>
  axios.post(`${url}candidate/merge-candidates`, payload);

export const mappedCandidateSearchAdvancedQuery = (
  jobCandidateInteraction: IJobCandidateInteraction
) => axios.post(`${url}mapping/unique-mappings/search`, jobCandidateInteraction);
export const getCandidateSmsLogs = (pageNumber: number) =>
  axios.get(`${url}sms-logs/${pageNumber}`);

export const saveDocuments = (documents: CandidateResume[]) =>
  axios.post(`${url}candidate/save-documents`, documents);

export const getCandidateProfileImages = (candidateIds: string[]) =>
  axios.post(`${url}candidate/profile-images`, candidateIds);

export const saveAutomatedEmailCampaign = (emailCampaign: IAutomatedEmailCampaignSetting) =>
  axios.post(`${url}automated/save-email-campaign-setting`, emailCampaign);

export const getAutomatedEmailCampaign = (type: AutomatedEmailCampaignType, key: string) =>
  axios.get(`${url}automated/get-email-campaign-setting`, { params: { type, key } });

export const getAutomatedEmailCampaignSettingList = (
  payload: IAutomatedEmailCampaignSettingListPayload
) => axios.post(`${url}automated/list-email-campaign-setting`, payload);

export const getAutomatedEmailSettingForJob = (jobId: string) =>
  axios.get(`${url}automated/get-email-campaign-setting/job`, { params: { jobId } });

export const getAutomatedEmailCampaignList = (payload: IAutomatedEmailCampaignListPayload) =>
  axios.post(`${url}email-campaign/list`, payload);

export const getJobAutomatedEmailLogsList = (payload: IAutomatedEmailCampaignListPayload) =>
  axios.post(`${url}email-campaign/job/email-list`, payload);

export const triggerAiAutomatedCampaign = (jobId: string, payload: IJobCandidateInteraction) =>
  axios.post(`${url}email-campaign/trigger/job/${jobId}`, payload);

export const getJobCampaignAnalytics = (payload: IAutomatedEmailCampaignListPayload) =>
  axios.post(`${url}email-campaign/analytics`, payload);

export const sentBulkEmailCampaignReplyMail = (payload: EmailLog) =>
  axios.post(`${url}email-campaign/bulk-email-scheduling/throttle`, payload);

export const saveAutomatedEmailCampaignConfigurationSettings = (
  payload: IAutomatedCampaignConfig
) => axios.post(`${url}automated-campaign/save-candidate-configuration`, payload);

export const getAutomatedEmailCampaignConfigurationSettings = () =>
  axios.get(`${url}automated-campaign/get-candidate-configuration`);

export const getCandidateEmailLogsByCandidateId = (payload: ICandidateEmailLogPayload) =>
  axios.post(`${url}get-candidate-email-logs`, payload);

export const getCandidateEmailLogsByCandidateIdAndCampaignCode = (
  campaignCode: string,
  candidateId: string
) => axios.get(`${url}get-candidate-email-logs`, { params: { campaignCode, candidateId } });
